import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { EFormeJuridique } from "common/enums/Office/EFormeJuridique";
import { EHabilitation } from "common/enums/Office/EHabilitation";
import { ELigneMetier } from "common/enums/Office/ELigneMetier";
import { ETypeConseiller } from "common/enums/Office/ETypeConseiller";
import AssociationProfessionelleResponseResource from "common/resources/Office/AssociationProfessionnelle/AssociationProfessionelleResponseResource";
import OfficeResponseResource from "common/resources/Office/OfficeResponseResource";
import OfficeUpdateRequestResource from "common/resources/Office/OfficeUpdateRequestResource";
import ProductSheetLightResponseResource from "common/resources/ProductSheet/ProductSheetLightResponseResource";
import { ValidationError } from "common/resources/Resource";
import Button from "components/elements/Button";
import InputGroup from "components/elements/InputGroup";
import Form from "components/materials/Form";
import CheckboxesInputElement, { CheckBoxesOption } from "components/materials/Form/CheckboxesInputElement";
import DatePickerInputElement from "components/materials/Form/DatePickerInputElement";
import InputElement, { EInputType } from "components/materials/Form/InputElement";
import MuiSelectInputElement from "components/materials/Form/MuiSelectInputElement";
import I18n from "components/materials/I18n";
import { useCallback, useEffect, useMemo, useState } from "react";
import AssociationProfessionnelleService from "services/AssociationProfessionnelleService";
import OfficeService from "services/OfficeService";
import ProductService from "services/ProductService";
import { container } from "tsyringe";
import FormUtils from "utils/FormUtils";
import { areAllOfficeProductsChecked, getHandPickedProductIds, toggleOfficeProduct } from "utils/officeProductSelection";

import classes from "./classes.module.scss";

type IProps = {
	office: OfficeResponseResource;
	loadOffice: () => void;
};
const baseTrad = I18n.asset.pages.partners.pages.offices_details;
const productService = container.resolve(ProductService);
const officeService = container.resolve(OfficeService);
const associationProfessionnelleService = container.resolve(AssociationProfessionnelleService);

export default function OfficeDetailsOffice(props: IProps) {
	const { office } = props;
	const [isReadonly, setIsReadonly] = useState(true);
	const [allChecked, setAllChecked] = useState(false);
	const [checkboxes, setCheckboxes] = useState<CheckBoxesOption[]>([]);
	const [errors, setErrors] = useState<ValidationError[]>([]);
	const [associationProfessionnelles, setAssociationProfessionnelles] = useState<AssociationProfessionelleResponseResource[]>([]);

	const [productSheets, setProductSheets] = useState<ProductSheetLightResponseResource[]>([]);
	const handPickedProductIds = useMemo(() => getHandPickedProductIds(productSheets), [productSheets]);
	const handleClickButton = (e: React.MouseEvent<HTMLButtonElement>) => {
		if (isReadonly) e.preventDefault();
		setIsReadonly(!isReadonly);
	};

	const onSubmit = useCallback(
		async (e: React.FormEvent<HTMLFormElement>, formData: { [key: string]: any }) => {
			e.preventDefault();
			setErrors([]);
			const officeResource = OfficeUpdateRequestResource.hydrate<OfficeUpdateRequestResource>({
				id: office.id,
				accountOwnerName: formData["accountOwnerName"],
				address: formData["address"],
				associationProfessionnelleId: formData["associationProfessionnelle"] as string,
				bic: formData["bic"],
				birthDateManager: new Date(formData["birthDateManager"]),
				capitalSocial: parseFloat(formData["capitalSocial"]),
				emailManager: formData["emailManager"],
				firstNameManager: formData["firstNameManager"],
				formeJuridique: formData["formeJuridique"] as EFormeJuridique,
				habilitations: FormUtils.getEnumValues<EHabilitation>(formData, "habilitations", EHabilitation),
				iban: formData["iban"],
				lastNameManager: formData["lastNameManager"],
				ligneMetiers: FormUtils.getEnumValues<ELigneMetier>(formData, "ligneMetiers", ELigneMetier),
				numeroOrias: formData["numeroOrias"],
				products: checkboxes.filter((option) => option.checked).map((option) => ({ id: option.value })),
				raisonSociale: formData["raisonSociale"],
				rcs: formData["rcs"],
				siret: formData["siret"],
				membreReseau: (formData["membreReseau"] as string) || undefined,
				typeConseiller: formData["typeConseiller"] as ETypeConseiller,
				emailAdminitratif: formData["emailAdminitratif"],
				phoneNumber: formData["phoneNumber"],
			});
			officeResource
				.validateOrReject()
				.then((resource) => officeService.update(office.id, resource))
				.then(() => {
					setIsReadonly(true);
					setErrors([]);
					props.loadOffice();
				})
				.catch((error: unknown) => {
					console.warn(error);
					setIsReadonly(false);
					if (error instanceof Array) {
						setErrors(error);
					}
				});
		},
		[checkboxes, office.id, props],
	);

	const onProductChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { value, checked } = event.target;
			// « Cocher toutes les SCPI » laisse de côté les produits attribués cabinet par cabinet (Hexa Scale Fund I).
			const newCheckboxesState = toggleOfficeProduct(checkboxes, handPickedProductIds, value, checked);
			setCheckboxes(newCheckboxesState);
			setAllChecked(areAllOfficeProductsChecked(newCheckboxesState, handPickedProductIds));
		},
		[checkboxes, handPickedProductIds],
	);

	useEffect(() => {
		productService.getAdminLastProductSheets().then(setProductSheets);
		associationProfessionnelleService.get().then(setAssociationProfessionnelles);
	}, []);

	useEffect(() => {
		if (productSheets.length === 0 || !office.products) return;
		const options = productSheets
			.map((productSheet) => ({
				value: productSheet.productId,
				label: productSheet.name,
				checked: office.products!.find((product) => product.id === productSheet.productId) ? true : false,
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
		setCheckboxes(options);
		setAllChecked(areAllOfficeProductsChecked(options, handPickedProductIds));
	}, [handPickedProductIds, office.products, productSheets]);

	return (
		<div className={classes["root"]}>
			<Form className={classes["form-container"]} onSubmit={onSubmit} errors={errors}>
				<div className={classes["row"]}>
					<div className={classes["col"]}>
						<InputGroup title={"Identité juridique"}>
							<InputElement
								name={"raisonSociale"}
								type={EInputType.TEXT}
								defaultValue={office.raisonSociale}
								readonly={isReadonly}
								label={baseTrad.identite_juridique.company_name}
							/>
							<InputElement name={"siret"} type={EInputType.TEXT} defaultValue={office.siret} readonly={isReadonly} label={baseTrad.identite_juridique.siret} />
							<MuiSelectInputElement
								name={"formeJuridique"}
								label={baseTrad.identite_juridique.forme_juridique}
								options={Object.keys(EFormeJuridique).map((key) => ({
									label: I18n.asset.enums.EFormeJuridique[EFormeJuridique[key as keyof typeof EFormeJuridique]],
									id: EFormeJuridique[key as keyof typeof EFormeJuridique],
								}))}
								defaultValue={{
									label: I18n.asset.enums.EFormeJuridique[office.formeJuridique],
									id: office.formeJuridique,
								}}
								readonly={isReadonly}
							/>
							<InputElement name={"rcs"} type={EInputType.TEXT} defaultValue={office.rcs} readonly={isReadonly} label={baseTrad.identite_juridique.rcs} />
							<InputElement
								name={"capitalSocial"}
								type={EInputType.NUMBER}
								defaultValue={office.capitalSocial}
								readonly={isReadonly}
								label={baseTrad.identite_juridique.capital_social}
							/>
							<InputElement
								name={"membreReseau"}
								type={EInputType.TEXT}
								defaultValue={office.membreReseau ?? undefined}
								readonly={isReadonly}
								label={baseTrad.identite_juridique.membre_reseau}
							/>
						</InputGroup>
						<InputGroup title={baseTrad.habilitations.title}>
							<MuiSelectInputElement
								name="typeConseiller"
								options={Object.keys(ETypeConseiller).map((key) => ({
									id: ETypeConseiller[key as keyof typeof ETypeConseiller],
									label: I18n.asset.enums.ETypeConseiller[ETypeConseiller[key as keyof typeof ETypeConseiller]],
								}))}
								defaultValue={{
									id: office.typeConseiller,
									label: I18n.asset.enums.ETypeConseiller[office.typeConseiller],
								}}
								readonly={isReadonly}
								label={baseTrad.habilitations.type_conseiller}
							/>
							<CheckboxesInputElement
								name="habilitations"
								options={Object.keys(EHabilitation).map((key) => ({
									label: I18n.asset.enums.EHabilitation[EHabilitation[key as keyof typeof EHabilitation]],
									value: EHabilitation[key as keyof typeof EHabilitation],
									defaultChecked: office.habilitations.includes(EHabilitation[key as keyof typeof EHabilitation]),
								}))}
								readonly={isReadonly}
								label={baseTrad.habilitations.habilitations}
							/>
							<MuiSelectInputElement
								name={"associationProfessionnelle"}
								readonly={isReadonly}
								options={associationProfessionnelles.map((association) => ({
									id: association.id,
									label: I18n.asset.enums.EAssociationProfesionnelle[association.name],
								}))}
								defaultValue={
									office.associationProfessionnelle && {
										id: office.associationProfessionnelle.id,
										label: I18n.asset.enums.EAssociationProfesionnelle[office.associationProfessionnelle.name],
									}
								}
								label={baseTrad.habilitations.association_professionnelle}
							/>
							<InputElement
								name={"numeroOrias"}
								type={EInputType.TEXT}
								readonly={isReadonly}
								defaultValue={office.numeroOrias}
								label={baseTrad.habilitations.numero_orias}
							/>
							<CheckboxesInputElement
								name={"ligneMetiers"}
								readonly={isReadonly}
								options={Object.keys(ELigneMetier).map((key) => ({
									label: I18n.asset.enums.ELigneMetier[ELigneMetier[key as keyof typeof ELigneMetier]],
									value: ELigneMetier[key as keyof typeof ELigneMetier],
									defaultChecked: office.ligneMetiers.includes(ELigneMetier[key as keyof typeof ELigneMetier]),
								}))}
								label={baseTrad.habilitations.ligne_metier}
							/>
						</InputGroup>
					</div>
					<div className={classes["col"]}>
						<InputGroup title={baseTrad.gerant.title}>
							<InputElement name={"lastNameManager"} type={EInputType.TEXT} readonly={isReadonly} defaultValue={office.lastNameManager} label={baseTrad.gerant.nom} />
							<InputElement
								name={"firstNameManager"}
								type={EInputType.TEXT}
								readonly={isReadonly}
								defaultValue={office.firstNameManager}
								label={baseTrad.gerant.prenom}
							/>
							<DatePickerInputElement
								name={"birthDateManager"}
								readonly={isReadonly}
								defaultValue={office.birthDateManager}
								label={baseTrad.gerant.date_naissance}
								isAbsoluteDate
							/>
							<InputElement
								name={"emailManager"}
								type={EInputType.TEXT}
								readonly={isReadonly}
								defaultValue={office.emailManager}
								label={baseTrad.gerant.email_gerant}
							/>
						</InputGroup>
						<InputGroup title={baseTrad.coordonnes_cabinet.title}>
							<InputElement
								name={"emailAdminitratif"}
								type={EInputType.TEXT}
								readonly={isReadonly}
								defaultValue={office.emailAdminitratif}
								label={baseTrad.coordonnes_cabinet.email_administratif}
							/>
							<InputElement
								name={"phoneNumber"}
								type={EInputType.TEXT}
								readonly={isReadonly}
								defaultValue={office.phoneNumber}
								label={baseTrad.coordonnes_cabinet.numero_telephone}
							/>
							<InputElement name={"address"} type={EInputType.TEXT} readonly={isReadonly} defaultValue={office.address} label={baseTrad.coordonnes_cabinet.adresse} />
						</InputGroup>
					</div>
					<div className={classes["col"]}>
						<InputGroup title={baseTrad.donnees_bancaires.title}>
							<InputElement
								name={"accountOwnerName"}
								type={EInputType.TEXT}
								readonly={isReadonly}
								defaultValue={office.accountOwnerName}
								label={baseTrad.donnees_bancaires.titulaire_compte}
							/>
							<InputElement name={"iban"} type={EInputType.TEXT} readonly={isReadonly} defaultValue={office.iban} label={baseTrad.donnees_bancaires.iban} />
							<InputElement name={"bic"} type={EInputType.TEXT} readonly={isReadonly} defaultValue={office.bic} label={baseTrad.donnees_bancaires.bic} />
						</InputGroup>
						<InputGroup title={baseTrad.produits_cabinet.title}>
							<CheckboxesInputElement
								name="products"
								options={[
									{
										label: I18n.asset.pages.create_partner.sub_pages.create_office.check_all_spci,
										value: "all",
										checked: allChecked,
									},
									...checkboxes,
								]}
								onChange={onProductChange}
								readonly={isReadonly}
							/>
						</InputGroup>
					</div>
				</div>
				<div className={classes["row"]}>
					<div className={classes["button-container"]}>
						<Button onClick={handleClickButton} icon={<PencilSquareIcon />} iconposition="right" type="submit">
							{isReadonly ? baseTrad.buttons.edit : baseTrad.buttons.save}
						</Button>
					</div>
				</div>
			</Form>
		</div>
	);
}
